import {ref, computed, onMounted, nextTick} from 'vue';
import {defineStore} from "pinia";
import {useQueryClient} from "@tanstack/vue-query";
import * as api from '../api'
import {setCookie, deleteCookie} from "@/shared/lib/utils/cookie";
import {getApiBaseUrl, isLocalDev} from "@/shared/lib/utils/environment";
import {useRoute} from "vue-router";
import {getMobileOperatingSystem, TPlatform} from "@/shared/lib/utils/platform";
import {resetOnBoardingCache} from "@/shared/api/markOnBoarding";
import {type Locale} from '@/shared/lib/i18n';

import {userKeys} from "./keys";
import {isAuth} from "./authState";
import {useSelectedCompany} from "./useSelectedCompany";

export const useUserStore = defineStore('userStore', () => {
    /**
     * Токен из куки поднимается асинхронно на старте приложения, поэтому пустой isAuth
     * до этого момента ещё ничего не значит: экранам, которые ведут гостя на вход,
     * нужно дождаться этого флага.
     */
    const isSessionResolved = ref<boolean>(false);
    const markSessionResolved = () => {
        isSessionResolved.value = true;
    };

    const route = useRoute();
    const queryClient = useQueryClient();

    const platform = ref<TPlatform | null>(null);

    // Компании живут в useSelectedCompany; здесь — только алиасы под старый API стора,
    // потребители переезжают на composable постепенно
    const {companies, selectedCompany, selectCompany, refreshCompanies, resetSelection} = useSelectedCompany();

    const setMainCompany = async (bin: string) => {
        selectCompany(bin);
    }

    const getUserInfo = async (uin?: string) => {
        await refreshCompanies(uin);
    }

    const getUserData = async () => {
        isAuth.value = true;
        // enabled: isAuth пересчитывается на flush реактивности, а refetchQueries пропускает
        // выключенные запросы — без nextTick профиль не попал бы в этот await
        await nextTick();
        await Promise.all([
            refreshCompanies(),
            // cancelRefetch: false — не отменяем уже летящий запрос, иначе получим второй такой же
            queryClient.refetchQueries({queryKey: userKeys.profile()}, {cancelRefetch: false}),
        ]);
    }

    /**
     * Единственный вход авторизации: форму показывает Auth из vue-ui-kit и отдаёт готовые
     * токены событием @token. Свои login/otp/смена пароля жили здесь до переезда на ui-kit.
     */
    const applyTokens = async (token: string, refreshToken: string) => {
        // refresh_token читает axios-интерцептор, но только в dev (см. hasRefreshSource)
        localStorage.setItem('refresh_token', refreshToken);

        if (isLocalDev()) {
            setCookie('GATEWAY', token, 1, '/');
        } else {
            const domain = `.${getApiBaseUrl()}`;
            setCookie('GATEWAY', token, 1, '/', domain);
        }

        await getUserData()
    }

    async function logout() {
        try {
            isAuth.value = false;
            // сброс до localStorage.clear(): в текущей вкладке useStorage не увидит очистку хранилища
            resetSelection();
            const ismetIdNote = localStorage.getItem('ismet-id-note');
            localStorage.clear()
            sessionStorage.clear();
            if (isLocalDev()) {
                deleteCookie('GATEWAY', '/');
            } else {
                const domain = `.${getApiBaseUrl()}`;
                deleteCookie('GATEWAY', '/', domain);
            }
            if (ismetIdNote) {
                localStorage.setItem('ismet-id-note', ismetIdNote);
            }
            // чтобы вход другим пользователем в этой же вкладке не показал кэш предыдущего
            queryClient.removeQueries({queryKey: userKeys.all});
            resetOnBoardingCache();
            api.logOut().then();
        } catch (e) {
            console.log(e)
        }
    }

    const isBlockingChangeCompany = computed(() => {
        return route.name == 'DraftDoc' || route.name == 'EditDocument' || route.name == 'CopyDocument';
    });

    onMounted(() => {
        platform.value = getMobileOperatingSystem();
    });

    function changeUserLang(lang: Locale) {
        if (isAuth.value) {
            api.saveUserLocale(lang).then()
        } else {
            const domain = `.${getApiBaseUrl()}`;
            //400 (9600) дней (так же приходит с бэка)
            setCookie('lang', lang, 9600, '/', domain)
        }
    }

    const isMobile = computed(() => platform.value ? ['android', 'ios'].includes(platform.value) : false);

    return {
        getUserInfo,
        isAuth,
        applyTokens,
        getUserData,
        logout,
        selectedContextCompany: selectedCompany,
        setMainCompany,
        contextCompanies: companies,
        isBlockingChangeCompany,
        isMobile,
        changeUserLang,
        isSessionResolved,
        markSessionResolved,
    }
});
